import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, X, Hash, MessageSquare, AppWindow } from 'lucide-react'
import { useSpaceStore } from '@os/spaces/web/stores/spaceStore'
import { useChannelStore } from '@/shell/stores/channelStore'

interface SearchResult {
  id: string
  type: 'channel' | 'message' | 'space'
  title: string
  subtitle?: string
  icon: React.ReactNode
  action: () => void
}

export default function GlobalSearch() {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const { spaces, setActiveChannel } = useSpaceStore()
  const { messages } = useChannelStore()

  // CMD/CTRL+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      }
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // W1.0: let the mobile top bar open the palette (no Cmd/Ctrl+K on phones).
  useEffect(() => {
    const open = () => setIsOpen(true)
    window.addEventListener('global-search:open', open)
    return () => window.removeEventListener('global-search:open', open)
  }, [])

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
      setQuery('')
      setResults([])
      setSelectedIndex(0)
    }
  }, [isOpen])

  const performSearch = useCallback((q: string) => {
    if (!q.trim()) {
      setResults([])
      return
    }

    const lower = q.toLowerCase()
    const found: SearchResult[] = []

    // Search channels
    spaces.forEach((space) => {
      space.channels?.forEach((ch) => {
        if (ch.name.toLowerCase().includes(lower)) {
          found.push({
            id: ch.id,
            type: 'channel',
            title: `#${ch.name}`,
            subtitle: space.name,
            icon: <Hash size={16} />,
            action: () => {
              setActiveChannel(ch.id)
              setIsOpen(false)
            },
          })
        }
      })
    })

    // Search spaces
    spaces.forEach((space) => {
      if (space.name.toLowerCase().includes(lower)) {
        found.push({
          id: space.id,
          type: 'space',
          title: space.name,
          subtitle: space.type === 'PUBLIC' ? 'Public space' : 'Private group',
          icon: <AppWindow size={16} />,
          action: () => {
            setActiveChannel(space.channels?.[0]?.id || null)
            setIsOpen(false)
          },
        })
      }
    })

    // Search messages
    Object.entries(messages).forEach(([channelId, channelMessages]) => {
      channelMessages.forEach((msg) => {
        if (msg.content?.toLowerCase().includes(lower)) {
          found.push({
            id: msg.id,
            type: 'message',
            title: msg.content.slice(0, 60) + (msg.content.length > 60 ? '...' : ''),
            subtitle: `Message`,
            icon: <MessageSquare size={16} />,
            action: () => {
              setActiveChannel(channelId)
              setIsOpen(false)
            },
          })
        }
      })
    })

    setResults(found.slice(0, 20))
    setSelectedIndex(0)
  }, [spaces, messages, setActiveChannel])

  useEffect(() => {
    performSearch(query)
  }, [query, performSearch])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((i) => (i + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((i) => (i - 1 + results.length) % results.length)
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      results[selectedIndex].action()
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center pt-[20vh] bg-black/50"
      onClick={() => setIsOpen(false)}
    >
      <div
        className="w-full max-w-xl bg-card rounded-xl shadow-2xl border border-white/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <Search size={20} className="text-white/40" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search channels, messages, apps..."
            className="flex-1 bg-transparent text-white text-base outline-none placeholder:text-white/30"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-white/40 hover:text-foreground">
              <X size={18} />
            </button>
          )}
          <kbd className="hidden sm:inline-block px-2 py-0.5 text-[10px] text-white/30 bg-white/10 rounded">
            ESC
          </kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {results.length === 0 && query.trim() && (
            <div className="px-4 py-8 text-center text-white/40 text-sm">
              No results found for &quot;{query}&quot;
            </div>
          )}

          {results.length === 0 && !query.trim() && (
            <div className="px-4 py-8 text-center text-white/40 text-sm">
              <p>Type to search across your workspace</p>
              <div className="flex justify-center gap-4 mt-3 text-[10px] text-white/20">
                <span># channels</span>
                <span>💬 messages</span>
                <span>📱 apps</span>
              </div>
            </div>
          )}

          {results.map((result, index) => (
            <button
              key={`${result.type}-${result.id}`}
              onClick={result.action}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition ${
                index === selectedIndex ? 'bg-primary/20' : 'hover:bg-white/5'
              }`}
            >
              <span className="text-white/50">{result.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{result.title}</div>
                {result.subtitle && (
                  <div className="text-[11px] text-white/40 truncate">{result.subtitle}</div>
                )}
              </div>
              <span className="text-[10px] text-white/20 uppercase">{result.type}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-t border-white/10 text-[10px] text-white/20">
          <span>{results.length} results</span>
          <div className="flex gap-3">
            <span>↑↓ navigate</span>
            <span>↵ select</span>
          </div>
        </div>
      </div>
    </div>
  )
}
